// How a paid feature introduces itself when the plan doesn't open it: what it
// is, how it helps, why it is better than a plain news feed — and a demo
// outline of its screen. This is product copy only; real data of a locked
// feature is never shown (the API doesn't send it).
import i18n from '@/i18n';
import type { Feature } from '@/lib/features';

export type FeatureInfo = {
  title: string;
  icon: string;
  /** what it is — one sentence */
  lead: string;
  /** how it helps / why it is better than a usual news service */
  points: [string, string, string];
  /** demo outline of the screen (blurred): [icon, line, sub-line]; numbers are masked */
  demo: Array<[string, string, string]>;
  /** where the feature lives on the site */
  to?: string;
  /** an illustrative message (made up, not real data) — what the user would get */
  example: string;
  /** how it works together with the rest of Orca */
  synergy: string;
};

// Texts live in the `plans` namespace (plans:feat.<feature>); here only what
// is not language: the icon, the route and the icons of the demo outline.
const META: Partial<Record<Feature, { icon: string; to?: string; demo: string[] }>> = {
  whale_alerts: { icon: 'whale', to: '/whales', demo: ['whale', 'whale', 'whale'] },
  wall_street: { icon: 'ws', to: '/wall-street', demo: ['ws', 'ws', 'macro'] },
  economic_calendar: { icon: 'cal', to: '/calendar', demo: ['cal', 'cal', 'cal'] },
  weekly_forecast: { icon: 'ai', to: '/signals', demo: ['price', 'link', 'alert'] },
  price_alerts: { icon: 'price', to: '/alerts', demo: ['price', 'price', 'bell'] },
  twitter: { icon: 'x', to: '/social', demo: ['x', 'x', 'x'] },
  youtube: { icon: 'yt', to: '/social#yt', demo: ['yt', 'yt'] },
  macro_news: { icon: 'macro', to: '/news?cat=macro', demo: ['macro', 'macro'] },
  investment_ideas: { icon: 'ai', to: '/#ideas', demo: ['ai', 'ai', 'ai'] },
  send_news_now: { icon: 'news', demo: [] },
};

type FeatText = { title: string; lead: string; points: [string, string, string]; example: string; synergy: string; demo: Array<[string, string]> };

/** Read in the current UI language on every access. */
export const FEATURE_INFO = new Proxy({} as Partial<Record<Feature, FeatureInfo>>, {
  get: (_, f: string) => {
    const m = META[f as Feature];
    if (!m) return undefined;
    const x = i18n.t(`plans:feat.${f}` as 'plans:feat.whale_alerts', { returnObjects: true }) as unknown as FeatText;
    return { ...x, icon: m.icon, to: m.to, demo: x.demo.map(([a, b], i) => [m.demo[i], a, b]) } satisfies FeatureInfo;
  },
});

/** «Что даёт Whale Alerts?» — the question Orca answers on a locked preview */
export const featureQuestion = (f: Feature) => i18n.t('plans:featureQ', { title: FEATURE_INFO[f]?.title ?? i18n.t('plans:fallback') });
/** «Покажи пример: Whale Alerts» */
export const exampleQuestion = (f: Feature) => i18n.t('plans:exampleQ', { title: FEATURE_INFO[f]?.title ?? i18n.t('plans:fallback') });
